import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { Box, IconButton, Typography, Chip } from '@mui/material';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { DataTable } from 'src/components/common/table/DataTable';
import { buildQueryParams } from 'src/components/common/table/dataTableQueryBuilder';
import {
	IColumn,
	ITableQueryState,
} from 'src/components/common/types/tableTypes';
import {
	IQuestionnaireTemplate,
	IQuestionnaireTemplateTableProps,
} from 'src/features/questionnaire_template/domain/types';
import {
	getQuestionnaireTemplates,
	QUESTIONNAIRE_TEMPLATE_LOADING_KEY,
} from 'src/features/questionnaire_template/store/questionnaireTemplateThunks';
import WithRoleAccess from 'src/routes/WithRoleAccess';
import { AppDispatch } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';

import { QuestionnaireTemplateTableActions } from './QuestionnaireTemplateTableActions';

export const QuestionnaireTemplateTable: React.FC<
	IQuestionnaireTemplateTableProps
> = ({ siteSlug }) => {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();

	const isLoading = useSelector(
		(state: { loading: ILoadingState }) =>
			state.loading.loadingStates[QUESTIONNAIRE_TEMPLATE_LOADING_KEY],
	);

	const [queryState, setQueryState] = useState<ITableQueryState>({
		sortBy: 'index',
		sortOrder: 'asc',
	});
	const [templates, setTemplates] = useState<IQuestionnaireTemplate[]>([]);
	const [clickedRow, setClickedRow] = useState<IQuestionnaireTemplate>();
	const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(
		null,
	);

	const handleMenuClick = (
		event: React.MouseEvent<HTMLElement>,
		questionnaireTemplate: IQuestionnaireTemplate,
	) => {
		setClickedRow(questionnaireTemplate);
		setAnchorElement(event.currentTarget);
	};

	const columns: IColumn<IQuestionnaireTemplate>[] = [
		{
			key: 'sn',
			label: 'S.N.',
			width: '7%',
			render: (questionnaireTemplate, index) => index + 1,
		},
		{
			key: 'name',
			label: 'Template Name',
			render: (questionnaireTemplate) => questionnaireTemplate.name,
		},
		{
			key: 'index',
			label: 'Index',
			sortable: true,
			render: (questionnaireTemplate) => questionnaireTemplate.index,
		},
		{
			key: 'published',
			label: 'Published',
			render: (questionnaireTemplate) => (
				<Chip
					label={questionnaireTemplate.published ? 'Yes' : 'No'}
					variant='outlined'
					size='medium'
				/>
			),
		},

		{
			key: 'created_at',
			label: 'Created At',
			sortable: true,
			render: (questionnaireTemplate) =>
				formatTimestamp(questionnaireTemplate.created_at),
		},
		{
			key: 'actions',
			label: 'Actions',
			render: (siteAccess) => (
				<IconButton onClick={(e) => handleMenuClick(e, siteAccess)}>
					<MoreVertOutlinedIcon />
				</IconButton>
			),
		},
	];

	const handleMenuClose = () => {
		setAnchorElement(null);
		setClickedRow(undefined);
	};

	useEffect(() => {
		const params = buildQueryParams(queryState);

		dispatch(
			getQuestionnaireTemplates({
				requestParams: params,
				siteSlug: siteSlug,
				onSuccess: (response) => {
					setTemplates(response);
				},
			}),
		);
	}, [queryState, siteSlug]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				padding: 3,
				backgroundColor: 'white',
				borderRadius: '4px',
				marginTop: '20px',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<Typography
					variant='h6'
					sx={{
						fontSize: '20px',
						fontWeight: 500,
						color: 'rgba(0, 0, 0, 0.87)',
					}}
				>
					Questionnaire Templates
				</Typography>

				<WithRoleAccess allowedSiteRoles={['super_admin', 'admin']}>
					<Box sx={{ width: '320px', height: '36px' }}>
						<PrimaryButton
							disabled={isLoading}
							onClick={() =>
								navigate(
									`/site/${siteSlug}/questionnaire-template-builder`,
									{
										state: {
											nextGeneratedIndex:
												templates.length + 1,
										},
									},
								)
							}
							sx={{
								height: '36px',
								minHeight: '36px',
								'& .MuiButton-root': {
									height: '36px',
									minHeight: '36px',
								},
								backgroundColor: '#BE3327',
								'&:hover': {
									backgroundColor: '#A52D23',
								},
							}}
						>
							Create Questionnaire Template
						</PrimaryButton>
					</Box>
				</WithRoleAccess>
			</Box>

			<DataTable
				columns={columns}
				data={templates}
				loadingKey={QUESTIONNAIRE_TEMPLATE_LOADING_KEY}
				emptyMessage='No questionnaire templates found'
				enablePagination={false}
				queryState={queryState}
				onQueryChange={(newQueryState) => {
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					const { page, ...rest } = newQueryState;
					setQueryState(rest);
				}}
			/>

			{clickedRow && (
				<QuestionnaireTemplateTableActions
					siteSlug={siteSlug}
					selectedRow={clickedRow}
					anchorElement={anchorElement}
					clearAnchorElement={() => setAnchorElement(null)}
					OnActionHit={() => setQueryState({ ...queryState })}
					onClose={handleMenuClose}
				/>
			)}
		</Box>
	);
};
